import apiClient from '@/lib/apiClient'
import { QueryClient, queryOptions, useMutation } from '@tanstack/react-query'
import { endpoints } from './endPoint'
import { USER_QUERY_KEYS } from './queryKeys'
import type z from 'zod'
import type { SortingState } from '@tanstack/react-table'
import axios from 'axios'
import { SORT_ORDER } from '@/constants'
import type { userSchema } from '@/schemas/user.schema'

export interface UserActivityParams {
  limit?: number
  page?: number
  sort?: SortingState
}

// Define the type for the suspend user request body
export type SuspendUserPayload = {
  duration: number
  reason: string
  notes?: string
}

// Types for creating a new user
type AddressPayload = {
  street: string
  city: string
  state: string
  zipCode: string
  country: string
}

export type CreateUserPayload = {
  username: string
  firstName: string
  lastName: string
  phoneNumber?: string
  roleIds: string[]
  email: string
  profilePicture?: string // This will be the KEY from the upload response
  address: AddressPayload
}

// Types for file upload
export type UploadUrlPayload = {
  presignPayload: {
    linkFor:
      | 'profile'
      | 'eventImages'
      | 'eventGuestProfiles'
      | 'activityHighlights'
      | 'advertisementSponsorLogo'
      | 'advertisementEventImage'
      | 'eventScheduleDocument'
      | 'eventInfoDocument'

    fileType: string
  }[]
}

export type UploadUrlResponse = {
  url: string
  index: number
  key: string
  fileType: string
  expiresOn: string
}[]

export const getUserProfile = async () => {
  const response = await apiClient.get(endpoints.user.profile)
  return response.data.data
}

// Define the type for the paginated API response
export type PaginatedUsersResponse = {
  success: boolean
  timestamp: string
  message: string
  data: {
    result: z.infer<typeof userSchema>[]
    totalCount: number
    page: number
    limit: number
    totalPages: number
    hasNextPage: boolean
    hasPrevPage: boolean
  }
}

export interface GetUserExportParams {
  search?: string
  roles?: string[]
  status?: string[]
  startDate?: string
  endDate?: string
  sort: SortingState
}

interface SuspendBulkUser {
  userIds: string[]
  duration: number
  reason: string
  notes?: string
}

export const fetchUsers = async (params: {
  limit?: number
  page?: number
  search?: string
  role?: string[]
  sort?: SortingState
  status?: string[]
  startDate?: string
  endDate?: string
}) => {
  try {
    const queryParams = new URLSearchParams()

    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.search) queryParams.append('search', params.search)
    if (params?.sort && params?.sort?.length > 0) {
      queryParams.append(
        'order',
        params?.sort?.[0].desc ? SORT_ORDER.DESC : SORT_ORDER.ASC
      )
    }
    if (params?.sort && params?.sort?.length > 0) {
      queryParams.append('orderOn', params?.sort?.[0].id || 'createdAt')
    }
    if (params.role) {
      params.role?.forEach(role => {
        queryParams.append('roles', role)
      })
    }
    if (params.status) {
      const updatedStatus = params.status.map(s => s.toLowerCase())
      updatedStatus?.forEach(status => {
        queryParams.append('statuses', status)
      })
    }
    if (params.startDate) {
      queryParams.append('startDate', params.startDate)
    }
    if (params.endDate) {
      queryParams.append('endDate', params.endDate)
    }

    const response = await apiClient.get(
      `/super-admin/users?${queryParams.toString()}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching users:', error)
    throw error
  }
}

export const suspendUser = async ({
  id,
  payload,
}: {
  id: string
  payload: SuspendUserPayload
}) => {
  const response = await apiClient.post(
    endpoints.superAdmin.suspendUser(id),
    payload
  )
  return response.data
}

export const blockUser = async (data: {
  id: string
  reason: string
  notes?: string
}) => {
  const response = await apiClient.post(
    endpoints.superAdmin.suspendUser(data.id),
    {
      reason: data.reason,
      notes: data.notes,
    }
  )
  return response.data
}
export const useSuspendUser = () => {
  return useMutation({
    mutationFn: suspendUser,
  })
}

export const getUploadUrl = async (
  payload: UploadUrlPayload
): Promise<UploadUrlResponse> => {
  const response = await apiClient.post(endpoints.upload.getUploadUrl, payload)
  return response.data.data
}

export const useGetUploadUrl = () => {
  return useMutation({
    mutationFn: getUploadUrl,
  })
}

export const getRoles = async () => {
  const response = await apiClient.get(endpoints.user.role)
  return response.data
}
export const uploadFile = async ({
  url,
  file,
}: {
  url: string
  file: File
}) => {
  await axios.put(url, file, {
    headers: {
      'Content-Type': file.type,
    },
  })
}

export const createUser = async (payload: CreateUserPayload) => {
  const response = await apiClient.post(
    endpoints.superAdmin.createUser,
    payload
  )
  return response.data
}

export const useCreateUser = () => {
  return useMutation({
    mutationFn: createUser,
  })
}

// 4. Update User Functions
export type UpdateUserPayload = {
  firstName: string
  lastName: string
  phoneNumber?: string
  roleIds: string[]

  profilePicture?: string
  address: AddressPayload
  email: string
}
// Add these new interfaces and functions

// ✅ Download URL interfaces
export interface DownloadUrlPayload {
  key: string
  expiresOn?: string
}

export interface DownloadUrlResponse {
  success: boolean
  message: string
  data: {
    url: string
    key: string
    expiresOn: string
  }
}

export const getDownloadUrl = async (
  payload: DownloadUrlPayload
): Promise<DownloadUrlResponse> => {
  const params = new URLSearchParams({ key: payload.key })
  if (payload.expiresOn) {
    params.append('expiresOn', payload.expiresOn)
  }

  const response = await apiClient.get<DownloadUrlResponse>(
    `${endpoints.upload.getDownloadUrl}?${params.toString()}`
  )
  return response.data
}

export const getImageUrlFromKey = async (
  imageKey: string
): Promise<{ url: string; key: string }> => {
  try {
    if (!imageKey || imageKey.trim() === '') {
      return { url: '', key: imageKey }
    }

    const response = await getDownloadUrl({ key: imageKey })

    return response.data
  } catch (error) {
    console.error('❌ Failed to get image URL for key:', imageKey, error)
    return { url: '', key: imageKey }
  }
}

// ... rest of existing code

export const getUserById = async (id: string) => {
  const response = await apiClient.get(endpoints.superAdmin.getUserById(id))
  return response.data
}

export const updateUser = async ({
  id,
  payload,
}: {
  id: string
  payload: UpdateUserPayload
}) => {
  const response = await apiClient.put(
    endpoints.superAdmin.editUser(id),
    payload
  )
  return response.data
}

export const useUpdateUser = () => {
  return useMutation({
    mutationFn: updateUser,
  })
}

export const userLoader = (queryClient: QueryClient) => () =>
  queryClient.fetchQuery({
    ...userQueryOptions.profile(),
    staleTime: 1000 * 60 * 2,
  })

export const userQueryOptions = {
  profile: () =>
    queryOptions({
      queryKey: USER_QUERY_KEYS.profile(),
      queryFn: getUserProfile,
    }),

  users: (params: {
    limit?: number
    page?: number
    search?: string
    role?: string[]
    sort?: SortingState
  }) =>
    queryOptions({
      queryKey: [...USER_QUERY_KEYS.users(), params],
      queryFn: () => fetchUsers(params),
    }),
}

// 5. Get various statistics about users in the system.
export const getUserStatistics = async (params: {
  month?: string
  year?: string
}) => {
  try {
    const queryParams = new URLSearchParams()
    if (params?.month) queryParams.append('month', params?.month?.toString())
    if (params?.year) queryParams.append('year', params?.year?.toString())

    const response = await apiClient.get(
      `${endpoints.superAdmin.getUserStatistics}?${queryParams.toString()}`
    )
    return response.data
  } catch (error) {
    console.error('Error while fetching user statistics', error)
    throw error
  }
}

// 6. Get user Activities by user id with
export const getUserActivity = async (
  userID: string,
  params: UserActivityParams
) => {
  const queryParams = new URLSearchParams()
  if (params.limit) queryParams.append('limit', params?.limit?.toString())
  if (params.page) queryParams.append('page', params?.page?.toString())
  if (params?.sort && params?.sort?.length > 0) {
    queryParams.append(
      'order',
      params?.sort?.[0].desc ? SORT_ORDER.DESC : SORT_ORDER.ASC
    )
  }
  if (params?.sort && params?.sort?.length > 0) {
    queryParams.append('orderOn', params?.sort?.[0].id || 'createdAt')
  }

  const url = `${endpoints.superAdmin.getUserActivity(userID)}?${queryParams.toString()}`
  const response = await apiClient.get(url)
  return response.data
}

export const unsuspendUser = async (userId: string) => {
  const response = await apiClient.post(
    endpoints.superAdmin.unsuspendUser(userId)
  )
  return response.data
}

// Individual unblock
export const unblockUser = async (userId: string) => {
  const response = await apiClient.post(
    endpoints.superAdmin.unblockUser(userId)
  )
  return response.data
}

// 7. Export list of all users
export const getUserExport = async (
  params: GetUserExportParams
): Promise<Blob> => {
  const queryParams = new URLSearchParams()

  if (params.search) {
    queryParams.append('search', params.search)
  }
  if (params.roles) {
    params.roles?.forEach(role => {
      queryParams.append('roles', role)
    })
  }
  if (params.status) {
    params.status?.forEach(status => {
      queryParams.append('statuses', status)
    })
  }
  if (params.startDate) {
    queryParams.append('startDate', params.startDate)
  }
  if (params.endDate) {
    queryParams.append('endDate', params.endDate)
  }
  if (params?.sort && params?.sort?.length > 0) {
    queryParams.append(
      'order',
      params?.sort?.[0].desc ? SORT_ORDER.DESC : SORT_ORDER.ASC
    )
  }
  if (params?.sort && params?.sort?.length > 0) {
    queryParams.append('orderOn', params?.sort?.[0].id || 'createdAt')
  }

  const response = await apiClient.get(endpoints.superAdmin.getExportUser, {
    params: queryParams,
    responseType: 'blob',
  })

  return response.data
}

// 8. Suspend or ban users in bulk
export const suspendBulkUser = async (payload: SuspendBulkUser) => {
  const response = await apiClient.put(
    endpoints.superAdmin.suspendBulkUser,
    payload
  )
  return response.data
}

export const blockBulkUser = async (data: {
  userIDs: string[]
  reason: string
  notes?: string
}) => {
  const response = await apiClient.put(endpoints.superAdmin.suspendBulkUser, {
    userIds: data.userIDs,
    reason: data.reason,
    notes: data.notes,
  })

  return response.data
}

// 9. Assign role to multiple users in bulk based on the provided criteria.
export const assignRoleBulkUser = async (payload: {
  userIds: string[]
  roleId: string
}) => {
  const response = await apiClient.put(
    endpoints.superAdmin.assignRoleBulkUser,
    payload
  )
  return response.data
}

export type UpdateUserProfilePayload = {
  firstName: string
  lastName: string
  phoneNumber: string
  profilePicture?: string
  address: AddressPayload
  gender?: string
  birthDate?: string
}

export const updateUserProfile = async (payload: UpdateUserProfilePayload) => {
  const response = await apiClient.put(endpoints.user.profile, payload)
  return response.data
}

export const getUserAttendedEvents = async (
  userID: string,
  params: UserActivityParams
) => {
  const queryParams = new URLSearchParams()
  if (params.limit) queryParams.append('limit', params?.limit?.toString())
  if (params.page) queryParams.append('page', params?.page?.toString())
  if (params?.sort && params?.sort?.length > 0) {
    queryParams.append(
      'order',
      params?.sort?.[0].desc ? SORT_ORDER.DESC : SORT_ORDER.ASC
    )
  }
  if (params?.sort && params?.sort?.length > 0) {
    queryParams.append('orderOn', params?.sort?.[0].id || 'createdAt')
  }

  const url = `${endpoints.superAdmin.getUserAttendedEvents(userID)}?${queryParams.toString()}`
  const response = await apiClient.get(url)
  return response.data
}

export const updateChatAdmin = async (id: string) => {
  try {
    const response = await apiClient.put(
      endpoints.superAdmin.setMessageAdmin(id)
    )
    return response.data
  } catch (error) {
    console.error('Error while updating chat admin', error)
    throw error
  }
}
